import React from "react"

import { ReactComponent as IconError } from "@common_assets/svg/cross-circle.svg"
import { Box, Button, CircularProgress, Grow, Typography } from "@mui/material"
import IconWrapper from "components/common/icon/iconWrapper"
import { useTranslation } from "react-i18next"

interface ISSOLoaderCardProps {
  showError: boolean
  loadingText: string
  errorText: string
  onReset: () => void
}

const SSOLoaderCard: React.FC<ISSOLoaderCardProps> = ({
  showError,
  loadingText,
  errorText,
  onReset,
}) => {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: (theme) => theme.palette.mockup.neutral95,
        zIndex: 9999,
      }}
    >
      <Box
        role="status"
        aria-live="polite"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        {!showError ? (
          <Grow in appear timeout={350}>
            <Box
              sx={{
                maxWidth: "560px",
                width: "90%",
                padding: 4,
                borderRadius: 3,
                backgroundColor: (theme) => theme.palette.common.white,
                boxShadow: (theme) => theme.shadows[8],
                textAlign: "center",
              }}
            >
              <Box sx={{ position: "relative", display: "inline-flex", mb: 2.5 }}>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={60}
                  thickness={4}
                  sx={{ color: "primary.light" }}
                />
                <CircularProgress
                  variant="indeterminate"
                  size={60}
                  thickness={4}
                  disableShrink
                  sx={{ color: "primary.main", position: "absolute", left: 0 }}
                />
              </Box>
              <Typography
                variant="subtitle1"
                color="mockup.darkBlue"
                sx={{ lineHeight: 1.4 }}
              >
                {loadingText}
              </Typography>
            </Box>
          </Grow>
        ) : (
          <Grow in appear timeout={350}>
            <Box
              sx={{
                maxWidth: "560px",
                width: "90%",
                padding: 4,
                borderRadius: 3,
                backgroundColor: (theme) => theme.palette.common.white,
                boxShadow: (theme) => theme.shadows[8],
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  backgroundColor: "error.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2.5,
                }}
              >
                <IconWrapper color="error.main" width="48px" height="48px">
                  <IconError width="100%" height="100%" />
                </IconWrapper>
              </Box>
              <Typography
                variant="subtitle1"
                color="mockup.darkBlue"
                role="alert"
                sx={{ lineHeight: 1.4, wordWrap: "break-word" }}
              >
                {errorText}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={onReset}
                fullWidth
                sx={{ mt: 3, borderRadius: 2, py: 1.25 }}
              >
                {t("noAuthFlow.backToLogin")}
              </Button>
            </Box>
          </Grow>
        )}
      </Box>
    </Box>
  )
}

export default SSOLoaderCard
